
/* The Outlands — story engine
   Lenis smooth scroll + GSAP ScrollTrigger for the choreography,
   Three.js for the watercolour plates and the weather (snow, stars, embers, petals). */
const $ = (s, r = root) => r.querySelector(s);
const $$ = (s, r = root) => [...r.querySelectorAll(s)];
const root = document.getElementById('sbs-outlands-homepage');
const assets = JSON.parse(root.querySelector('[data-outlands-assets]').textContent);
const asset = (key) => { if (!assets[key]) throw new Error('Unknown Outlands asset: '+key); return assets[key]; };
root.classList.add('js');

const REDUCE = matchMedia('(prefers-reduced-motion: reduce)').matches;
const COARSE = matchMedia('(hover: none), (pointer: coarse)').matches;
const gsap = window.gsap;
const ST = window.ScrollTrigger;
const HAS_GSAP = !!(gsap && ST);
if (HAS_GSAP) gsap.registerPlugin(ST);

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const imgReady = (img) => img && !img.complete ? new Promise((r) => { img.onload = img.onerror = r; }) : Promise.resolve();

/* -------------------------------------------------------------- shared state */
const mood = { night: 0, snow: 1, stars: 0, ember: 0, petal: 0 };
const mouse = { x: 0, y: 0, tx: 0, ty: 0, px: innerWidth / 2, py: innerHeight / 2 };
let scrollY = window.scrollY, scrollVel = 0, lenis = null;
let storeOverlayOpen = false;
const lenisTick = (t) => { if (lenis) lenis.raf(t * 1000); };

/* -------------------------------------------------------------- intro */
Promise.race([
  Promise.all([document.fonts ? document.fonts.ready : null, imgReady($('.cover-logo img'))]),
  wait(2200),
]).then(() => requestAnimationFrame(() => root.classList.add('is-loaded')));

/* -------------------------------------------------------------- split words */
$$('.split').forEach((el) => {
  const words = el.textContent.trim().split(/\s+/);
  el.setAttribute('aria-label', el.textContent.trim());
  el.innerHTML = words.map((w) => `<span class="w" aria-hidden="true">${w} </span>`).join('');
});

/* -------------------------------------------------------------- smooth scroll */
if (HAS_GSAP && !REDUCE && window.Lenis) {
  lenis = new window.Lenis({ lerp: 0.085, smoothWheel: true, eventsTarget: root });
  document.documentElement.classList.add('sbs-outlands-smooth');
  lenis.on('scroll', ST.update);
  window.__lenis = lenis;
  gsap.ticker.add(lenisTick);
  gsap.ticker.lagSmoothing(0);
}
function goTo(target) {
  const el = typeof target === 'string' ? $(target) : target;
  if (!el) return;
  if (lenis) lenis.scrollTo(el, { offset: 0, duration: 1.6 });
  else el.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth' });
}
root.addEventListener('click', (e) => {
  const a = e.target.closest('a[href^="#"]');
  if (!a) return;
  const id = a.getAttribute('href');
  if (id.length < 2 || !$(id)) return;
  e.preventDefault();
  goTo(id);
});

const storyScrollButton = $('[data-auto-scroll]');
let storyPlaying = false, storyTime = 0, storyPosition = 0;
function stopStoryScroll() {
  if (!storyPlaying && root.dataset.storyPlaying === 'false') return;
  storyPlaying = false;
  root.dataset.storyPlaying = 'false';
  if (storyScrollButton) {
    storyScrollButton.textContent = 'Wander On';
    storyScrollButton.setAttribute('aria-pressed', 'false');
    storyScrollButton.setAttribute('aria-label', 'Start automatic story scroll');
  }
}
function startStoryScroll() {
  if (REDUCE) { goTo('.prologue'); return; }
  if (storeOverlayOpen) return;
  storyPlaying = true;
  storyTime = performance.now();
  storyPosition = window.scrollY;
  if (lenis) { lenis.start(); lenis.scrollTo(storyPosition, { immediate: true }); }
  root.dataset.storyPlaying = 'true';
  storyScrollButton.textContent = 'Pause';
  storyScrollButton.setAttribute('aria-pressed', 'true');
  storyScrollButton.setAttribute('aria-label', 'Pause automatic story scroll');
}
function advanceStoryScroll() {
  if (!storyPlaying) return;
  if (document.hidden || storeOverlayOpen || $('.film.is-on, .drawer.is-on')) { stopStoryScroll(); return; }
  const now = performance.now();
  const dt = Math.min((now - storyTime) / 1000, .1);
  storyTime = now;
  const end = Math.max(0, Math.min(document.documentElement.scrollHeight - innerHeight, root.getBoundingClientRect().bottom + window.scrollY - innerHeight));
  if (storyPosition >= end) { stopStoryScroll(); return; }
  storyPosition = Math.min(end, storyPosition + dt * Math.max(45, Math.min(90, innerHeight / 12)));
  if (lenis) lenis.scrollTo(storyPosition, { immediate: true });
  else window.scrollTo({ top: storyPosition, behavior: 'instant' });
  if (storyPosition >= end) stopStoryScroll();
}
storyScrollButton?.addEventListener('click', () => storyPlaying ? stopStoryScroll() : startStoryScroll());
const interruptStoryScroll = (event) => {
  if (event.target instanceof Element && event.target.closest('[data-auto-scroll]')) return;
  stopStoryScroll();
};
document.addEventListener('wheel', () => stopStoryScroll(), { passive: true, capture: true });
document.addEventListener('pointerdown', interruptStoryScroll, { passive: true, capture: true });
document.addEventListener('touchstart', interruptStoryScroll, { passive: true, capture: true });
document.addEventListener('focusin', interruptStoryScroll);
document.addEventListener('keydown', (event) => {
  if ((event.key === 'Enter' || event.key === ' ') && event.target === storyScrollButton) return;
  stopStoryScroll();
});
document.addEventListener('visibilitychange', () => { if (document.hidden) stopStoryScroll(); });
addEventListener('pagehide', stopStoryScroll);
let storyViewportWidth = innerWidth;
addEventListener('resize', () => {
  if (innerWidth !== storyViewportWidth) stopStoryScroll();
  storyViewportWidth = innerWidth;
});
stopStoryScroll();

/* -------------------------------------------------------------- pointer */
addEventListener('pointermove', (e) => {
  mouse.px = e.clientX; mouse.py = e.clientY;
  mouse.tx = (e.clientX / innerWidth) * 2 - 1;
  mouse.ty = (e.clientY / innerHeight) * 2 - 1;
}, { passive: true });

const cursor = $('.cursor');
if (!COARSE && cursor) {
  const label = $('span', cursor);
  let cx = mouse.px, cy = mouse.py;
  const tick = () => {
    cx += (mouse.px - cx) * 0.2; cy += (mouse.py - cy) * 0.2;
    cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
    requestAnimationFrame(tick);
  };
  tick();
  root.addEventListener('pointerenter', () => cursor.classList.add('is-on'));
  root.addEventListener('pointerleave', () => cursor.classList.remove('is-on'));
  root.addEventListener('pointerover', (e) => {
    const t = e.target.closest('[data-cursor]');
    if (t) { label.textContent = t.dataset.cursor; cursor.classList.add('is-big'); }
  });
  root.addEventListener('pointerout', (e) => {
    const t = e.target.closest('[data-cursor]');
    if (t && !t.contains(e.relatedTarget)) cursor.classList.remove('is-big');
  });
  document.addEventListener('pointerleave', () => cursor.classList.remove('is-on'));
}

/* -------------------------------------------------------------- colour of the page (day → night → day) */
const C = {
  paper: [239, 240, 232], night: [21, 32, 61],
  ink: [58, 46, 40], nightInk: [241, 237, 225],
  soft: [108, 97, 89], nightSoft: [196, 199, 205],
};
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const rgb = (a) => `rgb(${a[0]},${a[1]},${a[2]})`;
let lastNight = -1;
function paintMood() {
  const n = Math.round(mood.night * 1000) / 1000;
  if (n === lastNight) return;
  lastNight = n;
  root.style.setProperty('--bg', rgb(mix(C.paper, C.night, n)));
  root.style.setProperty('--fg', rgb(mix(C.ink, C.nightInk, n)));
  root.style.setProperty('--fg-soft', rgb(mix(C.soft, C.nightSoft, n)));
  const l = mix(C.ink, C.nightInk, n);
  root.style.setProperty('--line', `rgba(${l[0]},${l[1]},${l[2]},.28)`);
}

/* -------------------------------------------------------------- chapter rail */
const rail = $('.rail');
function setRail(i) {
  $$('a', rail).forEach((a) => a.classList.toggle('is-active', +a.dataset.i === i));
}

/* -------------------------------------------------------------- cast peeks (chapter vi) */
const peek = $('.cast-peek');
$$('.cast button').forEach((b) => {
  const show = () => {
    $$('.cast button').forEach((x) => x.classList.toggle('is-on', x === b));
    peek.src = b.dataset.peek;
    const leaf = b.closest('.leaf').getBoundingClientRect();
    const r = b.getBoundingClientRect();
    peek.style.left = (r.left - leaf.left + r.width / 2) + 'px';
    peek.style.top = (r.bottom - leaf.top) + 'px';
    peek.classList.add('is-on');
  };
  b.addEventListener('pointerenter', show);
  b.addEventListener('focus', show);
  b.addEventListener('click', show);
  b.addEventListener('pointerleave', () => { peek.classList.remove('is-on'); b.classList.remove('is-on'); });
  b.addEventListener('blur', () => { peek.classList.remove('is-on'); b.classList.remove('is-on'); });
});

/* -------------------------------------------------------------- wardrobe layout */
const wardrobe = $('.wardrobe');
const track = $('.wardrobe-track');
const garments = $$('.garment', track);
const critters = Object.fromEntries($$('.w-critter', track).map((c) => [c.dataset.c, c]));
const pines = $$('.w-pine', track);
const segPaths = [$('.seg0', track), $('.seg1', track)];
let wardrobeMobile = false;

function layoutWardrobe() {
  wardrobeMobile = !HAS_GSAP || REDUCE || matchMedia('(max-width: 860px)').matches;
  wardrobe.classList.toggle('is-static', wardrobeMobile);
  const H = track.getBoundingClientRect().height || innerHeight;
  const v = H / 100;
  const intro = wardrobeMobile ? 0 : Math.max(470, 46 * v);
  const gH = 62 * v, gW = gH * (640 / 960);
  const pineH = 86 * v, pineW = pineH * (280 / 750);
  const lineY = 17 * v;
  const sag = 7 * v;
  const span0 = gW * 0.72 * 4.4, span1 = gW * 0.72 * 3.4;
  const x0 = intro + pineW * 0.6;
  const posts = [x0, x0 + span0, x0 + span0 + span1];
  pines.forEach((p, i) => {
    p.style.height = pineH + 'px';
    p.style.width = pineW + 'px';
    p.style.left = (posts[i] - pineW / 2) + 'px';
    p.style.bottom = (H * 0.06) + 'px';
  });
  const segs = [[posts[0], posts[1]], [posts[1], posts[2]]];
  const tie = H - H * 0.06 - pineH * 0.83;  // where the line meets the trunk
  const y0 = Math.max(lineY, tie);
  segPaths.forEach((p, i) => {
    const [a, b] = segs[i];
    p.setAttribute('d', `M${a} ${y0} Q${(a + b) / 2} ${y0 + sag * 2} ${b} ${y0}`);
  });
  const lineAt = (seg, t) => {
    const [a, b] = segs[seg];
    const x = a + (b - a) * t;
    const y = (1 - t) * (1 - t) * y0 + 2 * t * (1 - t) * (y0 + sag * 2) + t * t * y0;
    return [x, y];
  };
  garments.forEach((g, i) => {
    const [ax, ay] = g.dataset.attach.split(',').map(Number);
    const [x, y] = lineAt(+g.dataset.seg, +g.dataset.t);
    g.style.width = gW + 'px';
    g.style.left = (x - ax * gW) + 'px';
    g.style.top = (y - ay * gH) + 'px';
    g.style.transformOrigin = `${ax * 100}% ${ay * 100}%`;
    g.style.setProperty('--sw', (4.6 + (i % 3) * 0.9) + 's');
    g.style.setProperty('--sd', (-i * 1.3) + 's');
    g.style.setProperty('--r0', (-1.2 - (i % 2)) + 'deg');
    g.style.setProperty('--r1', (1.1 + (i % 3) * 0.5) + 'deg');
    g._x = x;
  });
  const gx = (n) => garments[n]._x;
  const ground = H * 0.94;
  const place = (name, h, x, lift = 0, z = 1) => {
    const c = critters[name];
    if (!c) return;
    c.style.height = h + 'px';
    c.style.left = x + 'px';
    c.style.top = (ground - h - lift) + 'px';
    c.style.zIndex = z;
  };
  place('house', 12 * v, posts[0] - pineW * 0.2 - 22 * v, 3 * v, 0);
  place('bear', 30 * v, gx(0) - gW * 0.62, -1 * v, 3);
  place('hare', 13 * v, gx(2) - 2 * v, 0, 3);
  place('fox', 21 * v, gx(3) - 6 * v, 0, 3);
  place('wolf', 24 * v, gx(4) - 16 * v, 0, 3);
  place('bark', 26 * v, posts[1] - 9 * v, 0, 1);
  place('flower', 18 * v, gx(6) + 6 * v, -2 * v, 3);
  const end = $('.w-end', track);
  const endX = posts[2] + pineW * 0.7;
  end.style.left = endX + 'px';
  const total = endX + Math.min(420, innerWidth * 0.8) + (wardrobeMobile ? 40 : 160);
  track.style.setProperty('--trackw', total + 'px');
  track.style.width = total + 'px';
  $('.w-line', track).setAttribute('viewBox', `0 0 ${total} ${H}`);
  $('.w-line', track).style.width = total + 'px';
}
garments.forEach((g) => {
  const near = g.dataset.near && critters[g.dataset.near];
  if (!near) return;
  g.addEventListener('pointerenter', () => { near.style.transform = 'translateY(-6px) rotate(-2deg)'; });
  g.addEventListener('pointerleave', () => { near.style.transform = ''; });
});
layoutWardrobe();

/* -------------------------------------------------------------- drawer */
const drawer = $('.drawer'), veil = $('.drawer-veil');
let lastFocus = null;
function openDrawer(g) {
  lastFocus = g;
  const d = g.dataset;
  $('figure img', drawer).src = $('img', g).src;
  $('figure img', drawer).alt = `${d.name}, painted in watercolour`;
  $('#drawer-title').textContent = d.name;
  $('.chip', drawer).style.background = d.hex;
  $('.fabric', drawer).textContent = d.fabric;
  $('.dline', drawer).textContent = d.line;
  $('.colour', drawer).textContent = d.colour;
  $('.desc', drawer).textContent = d.desc;
  $('.btn', drawer).href = d.url;
  drawer.classList.add('is-on'); veil.classList.add('is-on');
  drawer.setAttribute('aria-hidden', 'false');
  lenis && lenis.stop();
  $('.drawer-close').focus();
}
function closeDrawer() {
  drawer.classList.remove('is-on'); veil.classList.remove('is-on');
  drawer.setAttribute('aria-hidden', 'true');
  lenis && lenis.start();
  lastFocus && lastFocus.focus({ preventScroll: true });
}
garments.forEach((g) => g.addEventListener('click', () => openDrawer(g)));
$('.drawer-close').addEventListener('click', closeDrawer);
veil.addEventListener('click', closeDrawer);

/* -------------------------------------------------------------- film */
const LINES = $$('.chapter .line').map((l) => l.getAttribute('aria-label') || l.textContent);
const NUM = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix'];
const strip = $('.reel-strip');
strip.innerHTML = LINES.map((t, i) => `
  <button class="frame-card" type="button" data-film="${i}" aria-label="Play chapter ${NUM[i]}: ${t}">
    <span class="vid"><video muted loop playsinline preload="none" poster="${asset(`assets/video/story-${i + 1}.jpg`)}" data-src="${asset(`assets/video/story-${i + 1}.mp4`)}"></video>
      <span class="play" aria-hidden="true"><svg width="11" height="13" viewBox="0 0 10 12"><path d="M0 0l10 6-10 6z" fill="currentColor"/></svg></span></span>
    <span class="cap"><i>${NUM[i]}.</i><span>${t.length > 64 ? t.slice(0, 62).replace(/[\s,]+\S*$/, '') + '…' : t}</span></span>
  </button>`).join('');

const vio = new IntersectionObserver((entries) => entries.forEach((en) => {
  const v = en.target;
  if (en.isIntersecting) {
    if (!v.src) v.src = v.dataset.src;
    if (!REDUCE) v.play().catch(() => {});
  } else v.pause();
}), { threshold: 0.55 });
$$('video', strip).forEach((v) => vio.observe(v));

// drag to scroll the strip with a mouse
(() => {
  let down = false, sx = 0, sl = 0, moved = false;
  strip.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') return; down = true; moved = false; sx = e.clientX; sl = strip.scrollLeft; });
  addEventListener('pointermove', (e) => {
    if (!down) return;
    const dx = e.clientX - sx;
    if (Math.abs(dx) > 4) { moved = true; strip.classList.add('is-drag'); }
    strip.scrollLeft = sl - dx;
  });
  addEventListener('pointerup', () => { down = false; strip.classList.remove('is-drag'); });
  strip.addEventListener('click', (e) => { if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; } }, true);
})();

const film = $('.film');
const vids = $$('.film video');
const dotsBox = $('.film-dots');
dotsBox.innerHTML = NUM.map((n, i) => `<button type="button" aria-label="Chapter ${n}" data-i="${i}"><i></i></button>`).join('');
let filmIdx = 0, front = 0, muted = false;
function filmLoad(i, autoplay = true) {
  filmIdx = (i + 9) % 9;
  const cur = vids[front], nxt = vids[1 - front];
  nxt.src = asset(`assets/video/story-${filmIdx + 1}.mp4`);
  nxt.muted = muted;
  nxt.style.opacity = 0;
  if (autoplay) {
    // called inside the click so browsers allow sound; fall back to muted if refused
    nxt.play().catch(() => { nxt.muted = muted = true; soundBtn.textContent = 'Sound on'; nxt.play().catch(() => {}); });
  }
  const go = () => {
    nxt.style.opacity = 1; cur.style.opacity = 0;
    setTimeout(() => { cur.pause(); }, 650);
    front = 1 - front;
  };
  nxt.readyState >= 2 ? go() : nxt.addEventListener('loadeddata', go, { once: true });
  $$('button', dotsBox).forEach((b, k) => { $('i', b).style.transform = `scaleX(${k < filmIdx ? 1 : 0})`; });
}
vids.forEach((v) => {
  v.addEventListener('ended', () => { if (v === vids[front]) filmIdx < 8 ? filmLoad(filmIdx + 1) : closeFilm(); });
  v.addEventListener('timeupdate', () => {
    if (v !== vids[front] || !v.duration) return;
    const b = $$('button', dotsBox)[filmIdx];
    b && ($('i', b).style.transform = `scaleX(${v.currentTime / v.duration})`);
  });
});
const soundBtn = $('[data-act="sound"]', film);
function openFilm(i) {
  film.classList.add('is-on'); film.setAttribute('aria-hidden', 'false');
  lenis && lenis.stop();
  muted = false; soundBtn.textContent = 'Sound off';
  vids.forEach((v) => { v.style.opacity = 0; });
  filmLoad(i);
  $('.film-close').focus();
}
function closeFilm() {
  film.classList.remove('is-on'); film.setAttribute('aria-hidden', 'true');
  vids.forEach((v) => v.pause());
  lenis && lenis.start();
}
root.addEventListener('click', (e) => {
  const b = e.target.closest('[data-film]');
  if (b) openFilm(+b.dataset.film);
});
$('.film-close').addEventListener('click', closeFilm);
$('[data-act="prev"]', film).addEventListener('click', () => filmLoad(filmIdx - 1));
$('[data-act="next"]', film).addEventListener('click', () => filmLoad(filmIdx + 1));
dotsBox.addEventListener('click', (e) => { const b = e.target.closest('button'); b && filmLoad(+b.dataset.i); });
soundBtn.addEventListener('click', () => {
  muted = !muted; vids.forEach((v) => { v.muted = muted; });
  soundBtn.textContent = muted ? 'Sound on' : 'Sound off';
});
addEventListener('keydown', (e) => {
  if (e.key === 'Escape') { if (film.classList.contains('is-on')) closeFilm(); if (drawer.classList.contains('is-on')) closeDrawer(); }
  if (film.classList.contains('is-on')) {
    if (e.key === 'ArrowRight') filmLoad(filmIdx + 1);
    if (e.key === 'ArrowLeft') filmLoad(filmIdx - 1);
  }
});

/* ============================================================== CHOREOGRAPHY */
const plates = [];  // filled by GL
const nightRamps = [];
function initScroll() {
  if (!HAS_GSAP) return;
  const mm = gsap.matchMedia();

  if (REDUCE) {
    gsap.set('.cover-front', { rotateY: -178 }); gsap.set('.p-leaf', { rotateY: -176 }); gsap.set('.base-left', { opacity: 1 });
    initPassive(); return;
  }

  // ---- cover: walk into the painting
  mm.add('(min-width: 1px)', () => {
    const stage = $('.cover-stage');
    const layers = $$('.layer', stage);
    const setOrigins = () => {
      const vx = innerWidth * 0.5, vy = innerHeight * 0.66;
      layers.forEach((l) => {
        gsap.set(l, { clearProps: 'transform' });
        const r = l.getBoundingClientRect();
        const ox = ((vx - r.left) / r.width) * 100, oy = ((vy - r.top) / r.height) * 100;
        gsap.set(l, { transformOrigin: `${ox}% ${oy}%` });
      });
    };
    setOrigins();
    const tl = gsap.timeline({
      scrollTrigger: { trigger: '.cover', start: 'top top', end: '+=140%', scrub: 0.7, pin: true, anticipatePin: 1, onRefreshInit: setOrigins },
    });
    tl.to('.cover-title', { y: () => -innerHeight * 0.32, opacity: 0, filter: 'blur(8px)', ease: 'power1.in', duration: 0.45 }, 0)
      .to('.scroll-cue', { opacity: 0, duration: 0.12 }, 0);
    layers.forEach((l) => {
      const z = +l.dataset.z, out = +(l.dataset.out || 0);
      tl.to(l, { scale: 1 + Math.pow(z, 2.2) * 5.5, x: out * innerWidth * 0.35 * z, ease: 'power2.in', duration: 1 }, 0);
      if (z > 0.55) tl.to(l, { opacity: 0, duration: 0.3, ease: 'none' }, 0.55);
    });
    tl.to(stage, { opacity: 0, duration: 0.25, ease: 'none' }, 0.75);
  });

  // ---- words write themselves in
  $$('.split').forEach((el) => {
    gsap.from($$('.w', el), {
      opacity: 0, y: 10, filter: 'blur(5px)', duration: 1.1, ease: 'power2.out', stagger: 0.06,
      scrollTrigger: { trigger: el, start: 'top 82%' },
    });
  });
  $$('.chapter .numeral, .chapter .note, .cast').forEach((el) => {
    gsap.from(el, { opacity: 0, y: 14, duration: 1.2, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 88%' } });
  });
  gsap.from('.prologue .body, .sprig-row img', { opacity: 0, y: 16, duration: 1.4, stagger: 0.12, ease: 'power2.out', scrollTrigger: { trigger: '.prologue .body', start: 'top 85%' } });

  // ---- birds on chapter iii: ink drawn, then they drift
  $$('.birds path').forEach((p, i) => {
    const L = p.getTotalLength();
    gsap.set(p, { strokeDasharray: L, strokeDashoffset: L });
    gsap.to(p, { strokeDashoffset: 0, duration: 1.4, delay: i * 0.25, ease: 'power2.out', scrollTrigger: { trigger: '.birds', start: 'top 85%' } });
  });
  gsap.to('.birds', { x: 120, y: -60, ease: 'none', scrollTrigger: { trigger: '#ch-3', start: 'top bottom', end: 'bottom top', scrub: true } });

  // ---- plates drift, motifs float at their own depth
  $$('.chapter').forEach((ch) => {
    const plate = $('.plate', ch);
    gsap.fromTo(plate, { y: 50 }, { y: -50, ease: 'none', scrollTrigger: { trigger: ch, start: 'top bottom', end: 'bottom top', scrub: true } });
    gsap.fromTo($('.leaf', ch), { y: 30 }, { y: -30, ease: 'none', scrollTrigger: { trigger: ch, start: 'top bottom', end: 'bottom top', scrub: true } });
    $$('.motif', ch).forEach((m) => {
      const s = +m.dataset.speed || 1;
      gsap.fromTo(m, { y: 120 * s, rotate: -4 * s }, { y: -120 * s, rotate: 4 * s, ease: 'none', scrollTrigger: { trigger: ch, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
  });
  gsap.fromTo('.motif.grow', { scale: 0.4, transformOrigin: '50% 100%' }, { scale: 1, ease: 'none', scrollTrigger: { trigger: '#ch-8', start: 'top 80%', end: 'center 50%', scrub: true } });

  // ---- the book opens
  mm.add('(min-width: 1px)', () => {
    const book = $('.book');
    const pw = () => $('.base-right', book).getBoundingClientRect().width;
    const tl = gsap.timeline({ scrollTrigger: { trigger: '.book-scene', start: 'top top', end: '+=260%', scrub: 0.8, pin: true, anticipatePin: 1, invalidateOnRefresh: true } });
    gsap.from(book, { y: () => innerHeight * 0.4, rotateX: 22, opacity: 0, ease: 'power2.out', scrollTrigger: { trigger: '.book-scene', start: 'top 85%', end: 'top 5%', scrub: 0.6 } });
    gsap.from('.book-lead', { opacity: 0, y: 24, ease: 'power2.out', scrollTrigger: { trigger: '.book-scene', start: 'top 60%', end: 'top 10%', scrub: 0.6 } });
    tl.fromTo(book, { x: () => -pw() / 2 }, { x: () => -pw() / 2, duration: 0.12 }, 0)
      .to('.cover-front', { rotateY: -178, duration: 0.32, ease: 'power2.inOut' }, 0.12)
      .to(book, { x: 0, duration: 0.32, ease: 'power2.inOut' }, 0.12)
      .to('.base-left', { opacity: 1, duration: 0.02 }, 0.28)
      .to('.p-leaf', { rotateY: -176, duration: 0.28, ease: 'power2.inOut' }, 0.46)
      .to('.book-lead', { opacity: 0, y: -20, duration: 0.1 }, 0.72)
      .to(book, { scale: 1.55, x: () => -pw() * 0.5 * 1.55, y: () => innerHeight * 0.02, duration: 0.24, ease: 'power2.inOut' }, 0.76);
  });

  // ---- wardrobe: walk along the line
  mm.add('(min-width: 861px)', () => {
    layoutWardrobe();
    const dist = () => track.scrollWidth - innerWidth;
    const tween = gsap.to([track, '.w-intro-wrap'], { x: () => -dist(), ease: 'none',
      scrollTrigger: { trigger: wardrobe, start: 'top top', end: () => '+=' + dist(), pin: true, scrub: 0.9, anticipatePin: 1, invalidateOnRefresh: true, onRefreshInit: layoutWardrobe } });
    $$('.w-line path', track).forEach((p) => {
      const L = p.getTotalLength();
      gsap.fromTo(p, { strokeDasharray: L, strokeDashoffset: L }, { strokeDashoffset: 0, ease: 'none',
        scrollTrigger: { trigger: p, containerAnimation: tween, start: 'left 95%', end: 'right 75%', scrub: true } });
    });
    gsap.from('.w-intro > *', { opacity: 0, y: 20, stagger: 0.1, duration: 1.1, ease: 'power2.out', scrollTrigger: { trigger: wardrobe, start: 'top 70%' } });
    return () => gsap.set([track, '.w-intro-wrap'], { clearProps: 'transform' });
  });
  mm.add('(max-width: 860px)', () => { layoutWardrobe(); });

  // ---- colour swatches bloom in
  gsap.from('.swatch-paint', { opacity: 0, scale: 0.82, rotate: -5, duration: 1.3, ease: 'power3.out', stagger: 0.08, scrollTrigger: { trigger: '.swatches', start: 'top 80%' } });

  // ---- field notes
  gsap.fromTo('.field-bg', { yPercent: -6 }, { yPercent: 6, ease: 'none', scrollTrigger: { trigger: '.field', start: 'top bottom', end: 'bottom top', scrub: true } });
  gsap.from('.field-inner > *', { opacity: 0, y: 30, stagger: 0.15, duration: 1.3, ease: 'power2.out', scrollTrigger: { trigger: '.field-inner', start: 'top 85%' } });

  initPassive();
  function initPassive() {
  // ---- mood per section (weather), one active at a time
  $$('[data-fx]').forEach((sec) => {
    const fx = JSON.parse(sec.dataset.fx);
    const target = { snow: fx.snow ?? 0.3, stars: fx.stars ?? 0, ember: fx.ember ?? 0, petal: fx.petal ?? 0 };
    ST.create({
      trigger: sec, start: 'top 55%', end: 'bottom 55%',
      onToggle: (self) => { if (self.isActive) gsap.to(mood, { ...target, duration: 1.8, ease: 'sine.inOut', overwrite: 'auto' }); },
    });
  });

  // ---- night is a pure function of scroll: falls at chapter iv, lifts at vii, returns for the field notes
  const R = (trigger, start, end) => ST.create({ trigger, start, end });
  nightRamps.push(
    [R('#ch-4', 'top 95%', 'top 35%'), 1], [R('#ch-7', 'top 100%', 'top 40%'), -1],
    [R('#field', 'top 70%', 'top 10%'), 1], [R('#field', 'bottom 95%', 'bottom 45%'), -1],
  );
  // ---- rail
  ST.create({ trigger: '#story', start: 'top 60%', end: 'bottom 40%', onToggle: (s) => rail.classList.toggle('is-on', s.isActive) });
  $$('.chapter').forEach((ch, i) => ST.create({ trigger: ch, start: 'top 50%', end: 'bottom 50%', onToggle: (s) => s.isActive && setRail(i + 1) }));

  }
}

/* cover: mouse parallax lives on `translate`, the scroll timeline owns `transform` */
const coverLayers = $$('.cover-stage .layer');
function coverParallax() {
  if (REDUCE) return;
  if (scrollY > innerHeight * 2) return;
  coverLayers.forEach((l) => {
    const z = +l.dataset.z;
    l.style.translate = `${(-mouse.x * 26 * z).toFixed(2)}px ${(-mouse.y * 10 * z).toFixed(2)}px`;
  });
}

/* ============================================================== WEBGL */
async function initGL() {
  let THREE;
  try {
    THREE = await import(assets.three);
  } catch (e) { return null; }
  const canvas = $('#gl');
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false, premultipliedAlpha: true, powerPreference: 'high-performance' });
  } catch (e) { return null; }
  const DPR = Math.min(devicePixelRatio || 1, COARSE ? 1.5 : 1.75);
  renderer.setPixelRatio(DPR);
  renderer.setClearColor(0x000000, 0);
  const scene = new THREE.Scene();
  const cam = new THREE.OrthographicCamera(0, innerWidth, 0, -innerHeight, -100, 100);
  let W = innerWidth, H = innerHeight;
  const resize = () => {
    W = innerWidth; H = innerHeight;
    renderer.setSize(W, H, false);
    cam.right = W; cam.bottom = -H; cam.updateProjectionMatrix();
    U.uW.value = W; U.uH.value = H;
    if (typeof mist !== 'undefined') { mist.position.set(W / 2, -H / 2, 0); mist.scale.set(W, H, 1); mistMat.uniforms.uRes.value.set(W, H); }
  };
  const U = { uTime: { value: 0 }, uW: { value: W }, uH: { value: H }, uScroll: { value: 0 }, uPx: { value: DPR } };

  const NOISE = `
    float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
    float noise(vec2 p){ vec2 i = floor(p), f = fract(p); vec2 u = f*f*(3.-2.*f);
      return mix(mix(hash(i), hash(i+vec2(1.,0.)), u.x), mix(hash(i+vec2(0.,1.)), hash(i+vec2(1.,1.)), u.x), u.y); }
    float fbm(vec2 p){ float v = 0., a = .5; for (int i = 0; i < 5; i++){ v += a*noise(p); p = p*2.03 + 1.7; a *= .5; } return v; }`;

  /* ---------- watercolour plates */
  const plateGeo = new THREE.PlaneGeometry(1, 1);
  const plateVS = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.); }`;
  const plateFS = `
    precision highp float;
    varying vec2 vUv;
    uniform sampler2D uTex, uDepth;
    uniform float uProg, uTime, uSeed, uVel, uReady;
    uniform vec2 uMouse, uSize, uImg;
    ${NOISE}
    void main(){
      vec2 uv = vUv;
      float rp = uSize.x / uSize.y, ri = uImg.x / uImg.y;
      vec2 s = rp > ri ? vec2(1., ri / rp) : vec2(rp / ri, 1.);
      vec2 iuv = (uv - .5) * s + .5;
      float d = texture2D(uDepth, iuv).r;
      vec2 par = uMouse * (d - .42) * vec2(.026, .018);
      float sky = smoothstep(.3, .08, d);
      par.x += sky * (fbm(iuv * 3. + vec2(uTime * .025, 0.)) - .5) * .018;
      par.y += uVel * .014 * (d - .5);
      vec2 tuv = (iuv - .5) * .955 + .5 + par;
      vec4 col = texture2D(uTex, tuv);

      vec2 c = uv - .5;
      float box = 1. - max(abs(c.x), abs(c.y)) * 2.;
      float n1 = fbm(uv * vec2(2.6, 2.6 * uSize.y / uSize.x) + uSeed);
      float n2 = fbm(uv * 13. + uSeed * 3.1);
      float field = box * .95 + (n1 - .5) * .62 + (n2 - .5) * .16;
      float border = smoothstep(.015, .09, field);
      float thr = mix(1.05, -.12, uProg);
      float m = smoothstep(thr, thr + .05, field);
      float wet = smoothstep(.055, 0., abs(field - thr - .028)) * (1. - step(.995, uProg));
      float rim = smoothstep(.07, .0, abs(field - .055));
      col.rgb *= 1. - .26 * wet * m - .14 * rim;
      col.rgb = mix(col.rgb, col.rgb * vec3(1.02, 1.0, .97), .5);
      col.rgb *= .965 + .07 * n2;
      float a = m * border * uReady;
      gl_FragColor = vec4(col.rgb, a);
    }`;
  const loader = new THREE.TextureLoader();
  const blank = new THREE.DataTexture(new Uint8Array([128, 128, 128, 255]), 1, 1);
  blank.needsUpdate = true;

  $$('.plate').forEach((fig, i) => {
    const img = $('img', fig);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTex: { value: blank }, uDepth: { value: blank }, uProg: { value: REDUCE ? 1 : 0 }, uTime: U.uTime,
        uSeed: { value: +fig.dataset.seed || i }, uVel: { value: 0 }, uReady: { value: 0 },
        uMouse: { value: new THREE.Vector2() }, uSize: { value: new THREE.Vector2(1, 1) }, uImg: { value: new THREE.Vector2(1080, 1350) },
      },
      vertexShader: plateVS, fragmentShader: plateFS, transparent: true, depthTest: false, depthWrite: false,
    });
    const mesh = new THREE.Mesh(plateGeo, mat);
    mesh.visible = false;
    scene.add(mesh);
    const p = { fig, img, mesh, mat, loaded: false, loading: false, prog: REDUCE ? 1 : 0 };
    plates.push(p);
    if (HAS_GSAP && !REDUCE) {
      ST.create({ trigger: fig, start: 'top 95%', end: 'center 52%', scrub: 0.6, onUpdate: (s) => { p.prog = s.progress; } });
    } else p.prog = 1;
  });
  const FILE = location.protocol === 'file:';  // browsers block WebGL textures from file://, keep the <img> there
  const loadPlate = (p) => {
    if (p.loading || FILE) return;
    p.loading = true;
    let n = 0;
    const done = () => {
      if (++n < 2) return;
      p.loaded = true;
      gsap ? gsap.to(p.mat.uniforms.uReady, { value: 1, duration: 0.4 }) : (p.mat.uniforms.uReady.value = 1);
      p.fig.classList.add('gl-on');
    };
    loader.load(p.img.currentSrc || p.img.src, (t) => {
      t.anisotropy = 4;
      p.mat.uniforms.uTex.value = t;
      p.mat.uniforms.uImg.value.set(t.image.width, t.image.height);
      done();
    }, undefined, () => { p.loading = false; });
    loader.load(p.img.dataset.depth, (t) => { p.mat.uniforms.uDepth.value = t; done(); }, undefined, () => { done(); });
  };

  /* ---------- hero mist: drifting fbm bands, the cursor clears a path through them */
  const mistMat = new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uRes: { value: new THREE.Vector2(W, H) }, uM: { value: new THREE.Vector2(-999, -999) }, uAmt: { value: 1 }, uCol: { value: new THREE.Color('#F1F2EB') } },
    vertexShader: plateVS,
    fragmentShader: `precision highp float; varying vec2 vUv; uniform float uTime, uAmt; uniform vec2 uRes, uM; uniform vec3 uCol;
      ${NOISE}
      void main(){
        vec2 p = vUv * vec2(uRes.x / uRes.y, 1.);
        float y = 1. - vUv.y;
        float low = smoothstep(.5, .7, y) * (1. - smoothstep(.88, 1.02, y));
        float high = smoothstep(.12, .26, y) * (1. - smoothstep(.34, .5, y));
        float n = fbm(p * vec2(1.4, 4.2) + vec2(uTime * .03, 0.));
        float n2 = fbm(p * vec2(2.8, 6.5) - vec2(uTime * .045, uTime * .008));
        float fog = smoothstep(.42, .9, n * .75 + n2 * .45) * low + smoothstep(.55, .95, n2) * high * .55;
        float d = distance(vec2(vUv.x * uRes.x, y * uRes.y), uM);
        fog *= mix(.08, 1., smoothstep(50., 300., d));
        gl_FragColor = vec4(uCol, clamp(fog, 0., 1.) * .82 * uAmt);
      }`,
    transparent: true, depthTest: false, depthWrite: false,
  });
  const mist = new THREE.Mesh(plateGeo, mistMat);
  mist.renderOrder = 5;
  scene.add(mist);
  const mm2 = { x: -999, y: -999 };

  /* ---------- particles */
  const rnd = (n, f) => { const a = new Float32Array(n * 4); for (let i = 0; i < n; i++) f(a, i * 4); return a; };
  const points = (n, attr, vs, fs, uniforms, blending = THREE.NormalBlending) => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    g.setAttribute('aSeed', new THREE.BufferAttribute(attr, 4));
    const m = new THREE.ShaderMaterial({ uniforms: { ...U, ...uniforms }, vertexShader: vs, fragmentShader: fs, transparent: true, depthTest: false, depthWrite: false, blending });
    const pts = new THREE.Points(g, m);
    pts.frustumCulled = false;
    scene.add(pts);
    return m;
  };
  const R = Math.random;

  const snowN = COARSE ? 170 : 340;
  const snow = points(snowN, rnd(snowN, (a, i) => { a[i] = R(); a[i + 1] = R(); a[i + 2] = Math.pow(R(), 1.6); a[i + 3] = 0.7 + R() * 0.6; }), `
    attribute vec4 aSeed; uniform float uTime, uW, uH, uScroll, uPx, uAmt; varying float vA;
    void main(){
      float d = aSeed.z;
      float span = uH + 80.;
      float y = mod(aSeed.y * span + uTime * mix(16., 62., d) + uScroll * mix(.04, .3, d), span) - 40.;
      float sw = sin(uTime * mix(.25, .8, d) + aSeed.x * 40.) * mix(10., 34., d);
      float x = mod(aSeed.x * (uW + 80.) + sw + uTime * mix(3., 12., d), uW + 80.) - 40.;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(x, -y, 0., 1.);
      gl_PointSize = mix(2., 8.5, d * d) * aSeed.w * uPx;
      vA = step(fract(aSeed.x * 13.37 + aSeed.y * 7.13), uAmt) * mix(.5, 1., d);
    }`, `
    precision mediump float; uniform vec3 uCol; uniform float uOp; varying float vA;
    void main(){ vec2 c = gl_PointCoord - .5; float r = length(c);
      float a = smoothstep(.5, .12, r); gl_FragColor = vec4(uCol, a * vA * uOp); }`,
  { uAmt: { value: 1 }, uCol: { value: new THREE.Color('#9FB0B8') }, uOp: { value: 0.75 } });

  const starN = 260;
  const stars = points(starN, rnd(starN, (a, i) => { a[i] = R(); a[i + 1] = R(); a[i + 2] = R(); a[i + 3] = Math.pow(R(), 3); }), `
    attribute vec4 aSeed; uniform float uTime, uW, uH, uScroll, uPx, uAmt; varying float vA; varying float vS;
    void main(){
      float span = uH * 1.4;
      float y = mod(aSeed.y * span - uScroll * .03, span) - uH * .2;
      float x = aSeed.x * uW;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(x, -y, 0., 1.);
      vS = aSeed.w;
      gl_PointSize = (2. + aSeed.w * 9.) * uPx;
      float tw = .55 + .45 * sin(uTime * (.6 + aSeed.z * 2.4) + aSeed.w * 40. + aSeed.x * 9.);
      vA = uAmt * tw * smoothstep(0., .25, 1. - aSeed.y * .8);
    }`, `
    precision mediump float; varying float vA; varying float vS;
    void main(){ vec2 c = gl_PointCoord - .5; float r = length(c);
      float core = smoothstep(.22, .0, r);
      float glow = smoothstep(.5, .0, r) * .35;
      float cross = (smoothstep(.06, 0., abs(c.x)) + smoothstep(.06, 0., abs(c.y))) * smoothstep(.5, .1, r) * vS;
      vec3 col = mix(vec3(1., .96, .82), vec3(.95, .8, .45), vS);
      gl_FragColor = vec4(col, (core + glow + cross * .6) * vA); }`,
  { uAmt: { value: 0 } });

  const emberN = COARSE ? 60 : 110;
  const embers = points(emberN, rnd(emberN, (a, i) => { a[i] = R(); a[i + 1] = R(); a[i + 2] = R(); a[i + 3] = R(); }), `
    attribute vec4 aSeed; uniform float uTime, uW, uH, uPx, uAmt; varying float vA; varying float vT;
    void main(){
      float span = uH + 60.;
      float t = mod(aSeed.y * span + uTime * mix(22., 70., aSeed.z), span);
      float y = uH + 30. - t;
      float x = aSeed.x * uW + sin(uTime * 1.6 + aSeed.y * 20.) * 14. + sin(uTime * .5 + aSeed.x * 9.) * 40.;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(x, -y, 0., 1.);
      gl_PointSize = (2. + aSeed.w * 4.5) * uPx;
      vT = aSeed.w;
      float fl = .6 + .4 * sin(uTime * (6. + aSeed.z * 8.) + aSeed.x * 30.);
      vA = uAmt * fl * smoothstep(0., .25, t / span) * (1. - smoothstep(.6, 1., t / span)) * step(fract(aSeed.x * 7.7), .999);
    }`, `
    precision mediump float; varying float vA; varying float vT;
    void main(){ vec2 c = gl_PointCoord - .5; float r = length(c);
      vec3 col = mix(vec3(.84, .42, .14), vec3(.97, .74, .3), vT);
      gl_FragColor = vec4(col, smoothstep(.5, .05, r) * vA); }`,
  { uAmt: { value: 0 } });

  const petalN = COARSE ? 50 : 90;
  const petals = points(petalN, rnd(petalN, (a, i) => { a[i] = R(); a[i + 1] = R(); a[i + 2] = R(); a[i + 3] = R(); }), `
    attribute vec4 aSeed; uniform float uTime, uW, uH, uPx, uAmt, uScroll; varying float vA; varying float vK; varying float vR;
    void main(){
      float span = uH + 80.;
      float y = mod(aSeed.y * span + uTime * mix(18., 40., aSeed.z) + uScroll * .12, span) - 40.;
      float x = mod(aSeed.x * (uW + 80.) + sin(uTime * .7 + aSeed.y * 12.) * 50. + uTime * 10., uW + 80.) - 40.;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(x, -y, 0., 1.);
      gl_PointSize = (7. + aSeed.z * 9.) * uPx;
      vK = aSeed.w; vR = uTime * (.6 + aSeed.z) + aSeed.x * 20.;
      vA = uAmt;
    }`, `
    precision mediump float; varying float vA; varying float vK; varying float vR;
    void main(){ vec2 c = gl_PointCoord - .5; float s = sin(vR), co = cos(vR);
      c = mat2(co, -s, s, co) * c; c.x *= 1.9;
      float a = smoothstep(.5, .3, length(c));
      vec3 col = vK < .4 ? vec3(.9, .66, .63) : (vK < .75 ? vec3(.88, .57, .25) : vec3(.66, .71, .55));
      gl_FragColor = vec4(col, a * vA * .85); }`,
  { uAmt: { value: 0 } });

  /* ---------- shooting stars */
  const streakMat = new THREE.ShaderMaterial({
    uniforms: { uLife: { value: 0 } },
    vertexShader: plateVS,
    fragmentShader: `precision mediump float; varying vec2 vUv; uniform float uLife;
      void main(){ float tail = pow(vUv.x, 2.4); float w = 1. - abs(vUv.y - .5) * 2.;
        float head = smoothstep(.9, 1., vUv.x);
        vec3 col = mix(vec3(.95, .82, .5), vec3(1., .97, .85), head);
        gl_FragColor = vec4(col, (tail * w + head * .8 * w) * uLife); }`,
    transparent: true, depthTest: false, depthWrite: false,
  });
  const streaks = [0, 1, 2].map(() => {
    const m = new THREE.Mesh(plateGeo, streakMat.clone());
    m.visible = false; scene.add(m);
    return { m, t: 0, life: 0, x: 0, y: 0, vx: 0, vy: 0 };
  });
  let nextStreak = 2;

  /* ---------- loop */
  resize();
  addEventListener('resize', resize);
  const snowDay = new THREE.Color('#AEBEC6'), snowNight = new THREE.Color('#FFFFFF');
  let last = performance.now();
  const frame = () => {
    const now = performance.now();
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    U.uTime.value += dt;
    U.uScroll.value = scrollY;
    snow.uniforms.uAmt.value = mood.snow;
    snow.uniforms.uCol.value.copy(snowDay).lerp(snowNight, mood.night);
    snow.uniforms.uOp.value = 0.5 + 0.42 * mood.night;
    stars.uniforms.uAmt.value = mood.stars * Math.max(mood.night, 0.0);
    embers.uniforms.uAmt.value = mood.ember;
    petals.uniforms.uAmt.value = mood.petal;

    // mist over the cover only
    const heroK = Math.max(0, 1 - scrollY / (innerHeight * 0.85)) * (1 - mood.night);
    mist.visible = heroK > 0.01;
    mistMat.uniforms.uAmt.value = heroK;
    if (mm2.x < -900) { mm2.x = mouse.px; mm2.y = mouse.py; }
    mm2.x += (mouse.px - mm2.x) * 0.08; mm2.y += (mouse.py - mm2.y) * 0.08;
    mistMat.uniforms.uM.value.set(COARSE ? -999 : mm2.x, COARSE ? -999 : mm2.y);

    // plates: read rects, then write
    const rects = plates.map((p) => p.fig.getBoundingClientRect());
    plates.forEach((p, i) => {
      const r = rects[i];
      const near = r.bottom > -H * 1.2 && r.top < H * 2.2;
      if (near && !p.loading) loadPlate(p);
      const vis = r.bottom > -20 && r.top < H + 20 && p.loaded;
      p.mesh.visible = vis;
      if (!vis) return;
      p.mesh.position.set(r.left + r.width / 2, -(r.top + r.height / 2), 0);
      p.mesh.scale.set(r.width, r.height, 1);
      const u = p.mat.uniforms;
      u.uSize.value.set(r.width, r.height);
      u.uProg.value += (p.prog - u.uProg.value) * 0.12;
      u.uMouse.value.set(mouse.x, -mouse.y);
      u.uVel.value = Math.max(-1, Math.min(1, scrollVel / 3500));
    });

    // shooting stars
    if (mood.night * mood.stars > 0.6 && !REDUCE) {
      nextStreak -= dt;
      if (nextStreak <= 0) {
        const s = streaks.find((k) => k.life <= 0);
        if (s) {
          const ang = (200 + R() * 18) * Math.PI / 180;
          const sp = 700 + R() * 500;
          s.x = W * (0.35 + R() * 0.7); s.y = -H * (0.02 + R() * 0.35);
          s.vx = Math.cos(ang) * sp; s.vy = Math.sin(ang) * sp;
          s.t = 0; s.life = 0.9 + R() * 0.5;
          s.len = 140 + R() * 140;
          s.m.visible = true;
        }
        nextStreak = 1.4 + R() * 2.6;
      }
    }
    streaks.forEach((s) => {
      if (s.life <= 0) { s.m.visible = false; return; }
      s.t += dt;
      const k = s.t / s.life;
      if (k >= 1) { s.life = 0; s.m.visible = false; return; }
      s.x += s.vx * dt; s.y += s.vy * dt;
      const a = Math.atan2(s.vy, s.vx);
      s.m.rotation.z = a;
      s.m.scale.set(s.len, 2.2, 1);
      s.m.position.set(s.x - Math.cos(a) * s.len / 2, s.y - Math.sin(a) * s.len / 2, 0);
      s.m.material.uniforms.uLife.value = Math.sin(k * Math.PI) * mood.night;
    });

    renderer.render(scene, cam);
  };
  return { frame };
}

/* ============================================================== MAIN LOOP */
let gl = null;
let clipTop = -1, clipBottom = -1;
function clipArtwork() {
  const bounds = root.getBoundingClientRect();
  const header = document.getElementById('shopify-section-header');
  const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  const top = Math.ceil(Math.min(innerHeight, Math.max(0, bounds.top, Math.min(headerBottom, innerHeight))));
  const bottom = Math.ceil(Math.min(innerHeight, Math.max(0, innerHeight - bounds.bottom)));
  if (top !== clipTop) { root.style.setProperty('--outlands-clip-top', top+'px'); clipTop=top; }
  if (bottom !== clipBottom) { root.style.setProperty('--outlands-clip-bottom', bottom+'px'); clipBottom=bottom; }
}
function loop() {
  if (storeOverlayOpen) return;
  advanceStoryScroll();
  clipArtwork();
  const y = lenis ? lenis.scroll : window.scrollY;
  scrollVel += ((y - scrollY) * 60 - scrollVel) * 0.15;
  scrollY = y;
  if (COARSE) { const t = performance.now() / 1000; mouse.tx = Math.sin(t * 0.25) * 0.55; mouse.ty = Math.cos(t * 0.19) * 0.35; }
  mouse.x += (mouse.tx - mouse.x) * 0.06;
  mouse.y += (mouse.ty - mouse.y) * 0.06;
  if (nightRamps.length) {
    let n = 0;
    nightRamps.forEach(([st, sign]) => { n += sign * Math.min(1, Math.max(0, (y - st.start) / Math.max(1, st.end - st.start))); });
    mood.night = Math.min(1, Math.max(0, n));
  }
  paintMood();
  coverParallax();
  gl && gl.frame();
}
if (HAS_GSAP) gsap.ticker.add(loop);
else { const raf = () => { loop(); requestAnimationFrame(raf); }; raf(); }

if (HAS_GSAP) gsap.context(initScroll, root);
else initScroll();
initGL().then((g) => { gl = g; if (g) root.classList.add('has-gl'); });

addEventListener('resize', () => { if (!HAS_GSAP || REDUCE) layoutWardrobe(); });
addEventListener('load', () => { HAS_GSAP && ST.refresh(); });


document.fonts?.ready.then(() => { if (HAS_GSAP) ST.refresh(); });
setTimeout(() => { if (HAS_GSAP) ST.refresh(); }, 2500);
window.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#sbs-outlands-homepage')) location.reload();
});
root.dataset.outlandsReady = 'true';
// Let the store's native search and navigation own the screen while open.
function syncStoreOverlay() {
  const open = document.body.classList.contains('show-search') || document.body.classList.contains('reveal-mobile-nav');
  root.dataset.storeOverlay = open ? 'open' : 'closed';
  if (open === storeOverlayOpen) return;
  storeOverlayOpen = open;
  if (open) {
    stopStoryScroll();
    if (lenis) lenis.stop();
    if (HAS_GSAP) { gsap.ticker.remove(lenisTick); gsap.ticker.remove(loop); gsap.ticker.sleep(); }
    if (document.body.classList.contains('show-search')) {
      const panel = document.getElementById('main-search');
      const input = panel && panel.querySelector('input[name="q"]');
      const deadline = performance.now() + 5000;
      const focusSearch = () => {
        if (!input || !document.body.classList.contains('show-search')) return;
        if (getComputedStyle(panel).visibility === 'visible') input.focus({ preventScroll:true });
        else if (performance.now() < deadline) requestAnimationFrame(focusSearch);
      };
      requestAnimationFrame(focusSearch);
    }
  } else {
    if (lenis && !film.classList.contains('is-on') && !drawer.classList.contains('is-on')) lenis.start();
    if (HAS_GSAP) { if (lenis) gsap.ticker.add(lenisTick); gsap.ticker.add(loop); gsap.ticker.wake(); }
  }
}
new MutationObserver(syncStoreOverlay).observe(document.body, { attributes:true, attributeFilter:['class'] });
syncStoreOverlay();
